/**
 * Helpers for reading npt-api error responses.
 *
 * The API reports failures as an array of `{ title, detail, ... }` (sometimes wrapped as
 * `{ errors: [...] }`, sometimes a plain `{ message }`).
 *
 * NPT-2110: error kinds are recognised by status and stable code instead of by matching text.
 * npt-api used to return raw MySQL text for database errors (`400 Duplicate entry '3' for key ...`),
 * and the program form matched on "Duplicate". The API now returns `409` with
 * `errors[0].errorCode === 'DUPLICATE_ENTRY'` and a safe message. Both shapes are accepted here, so
 * web and API can deploy in either order.
 */
export const DUPLICATE_ENTRY = 'DUPLICATE_ENTRY';

type ApiErrorItem = { code?: number; title?: string; errorCode?: string; detail?: unknown };
type ApiErrorBody = ApiErrorItem[] | { errors?: ApiErrorItem[]; message?: unknown } | null | undefined;

const errorBody = (error: unknown): ApiErrorBody =>
   (error as { response?: { data?: ApiErrorBody } } | null | undefined)?.response?.data;

/** First error item, whether the body is the bare array or wrapped as `{ errors: [...] }`. */
const firstApiError = (error: unknown): ApiErrorItem | undefined => {
   const data = errorBody(error);
   return Array.isArray(data) ? data[0] : data?.errors?.[0];
};

/**
 * Returns the message to show a user, or undefined when the error carries none (a dropped
 * connection, say), so callers can `getApiErrorMessage(err) || fallback`.
 */
export const getApiErrorMessage = (error: unknown): string | undefined => {
   const data = errorBody(error);
   const message = firstApiError(error)?.detail ?? (Array.isArray(data) ? undefined : data?.message);
   return typeof message === 'string' && message.trim() ? message : undefined;
};

export const isDuplicateEntryError = (err: unknown): boolean => {
   const first = firstApiError(err);
   if (first?.errorCode === DUPLICATE_ENTRY) return true;
   // Older API builds: raw driver text in the detail.
   return typeof first?.detail === 'string' && first.detail.includes('Duplicate');
};

/** 429 from the API (for example the guest RSVP limit). */
export const isRateLimitedError = (err: unknown): boolean =>
   (err as { response?: { status?: number } } | null | undefined)?.response?.status === 429;
